'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useDigitalScribe } from '@/lib/hooks/useDigitalScribe';

type CaptureMode = 'Document' | 'Object';

type CaptureValues = {
  title: string;
  category: string;
  historicalDate: string;
  description: string;
  estimatedDecade: string;
  condition: string;
  provenance: string;
  donorName: string;
};

const categories = [
  'Lodge Records',
  'Charters & Bylaws',
  'Physical Artifacts',
  'Regalia & Textiles',
  'Member History',
  'Building & Property',
];

const conditions = ['Excellent', 'Good', 'Fair', 'Poor', 'Fragile — Handle with Care'];

export default function DocumentCaptureForm() {
  const [mode, setMode] = useState<CaptureMode>('Document');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const fileRef = useRef<HTMLInputElement>(null);

  const { result: scribeResult, isAnalysing, error: scribeError, analyseFile, reset: resetScribe } = useDigitalScribe();

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<CaptureValues>({
    defaultValues: {
      title: '',
      category: '',
      historicalDate: '',
      description: '',
      estimatedDecade: '',
      condition: '',
      provenance: '',
      donorName: '',
    },
  });

  // Show scribe errors as toast
  useEffect(() => {
    if (scribeError) {
      toast.error(`Digital Scribe: ${scribeError}`);
    }
  }, [scribeError]);

  // Auto-populate form fields when scribe result arrives
  useEffect(() => {
    if (!scribeResult) return;
    const dateEntity = scribeResult.entities.find((e) => e.type === 'date');
    if (dateEntity) setValue('historicalDate', dateEntity.value);
    if (scribeResult.summary) setValue('description', scribeResult.summary);
  }, [scribeResult, setValue]);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files).filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)
    );
    setUploadedFiles((prev) => [...prev, ...arr]);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeFile = (idx: number) => {
    setUploadedFiles((f) => f.filter((_, i) => i !== idx));
    resetScribe();
  };

  const handleRunScribe = async () => {
    const imageFile = uploadedFiles.find((f) => f.type.startsWith('image/'));
    if (!imageFile) {
      toast.error('Please upload an image file (JPG, PNG, or WEBP) to run the Digital Scribe');
      return;
    }
    toast.info('Digital Scribe is analysing your document…');
    const result = await analyseFile(imageFile);
    if (result) {
      toast.success(`Digital Scribe complete — ${result.overallConfidence}% confidence`);
    }
  };

  const onSubmit = async (data: CaptureValues) => {
    if (uploadedFiles.length === 0) {
      toast.error('Please attach at least one file before submitting');
      return;
    }
    setIsSubmitting(true);
    // BACKEND INTEGRATION: POST /api/records with FormData including files + metadata
    await new Promise((r) => setTimeout(r, 2000));
    setIsSubmitting(false);
    toast.success('Record captured — AI transcription queued for processing');
    reset();
    resetScribe();
    setUploadedFiles([]);
    setStep(1);
  };

  const confidenceColor = (c: number) =>
    c >= 85 ? 'var(--success)' : c >= 70 ? 'var(--warning)' : 'var(--danger)';

  const entityTypeColors: Record<string, { bg: string; color: string; icon: string }> = {
    date: { bg: 'var(--info-bg)', color: 'var(--info)', icon: 'CalendarIcon' },
    name: { bg: 'rgba(201, 168, 76, 0.15)', color: '#7A5C1E', icon: 'UserIcon' },
    place: { bg: 'var(--success-bg)', color: 'var(--success)', icon: 'MapPinIcon' },
    amount: { bg: 'var(--warning-bg)', color: 'var(--warning)', icon: 'CurrencyDollarIcon' },
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 2xl:grid-cols-5 gap-6">
      {/* Left: Capture panel */}
      <div className="xl:col-span-2 space-y-5">
        {/* Mode toggle */}
        <div className="masonic-card p-5">
          <h2 className="font-serif text-base font-semibold mb-4" style={{ color: 'var(--foreground)' }}>
            Capture Mode
          </h2>
          <div className="flex rounded-lg p-1" style={{ background: 'var(--muted)' }}>
            {(['Document', 'Object'] as CaptureMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-all duration-150"
                style={{
                  background: mode === m ? 'var(--primary)' : 'transparent',
                  color: mode === m ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                  boxShadow: mode === m ? '0 1px 3px rgba(27,42,74,0.2)' : 'none',
                }}
              >
                <Icon name={m === 'Document' ? 'DocumentTextIcon' : 'CubeIcon'} size={16} />
                {m} Mode
              </button>
            ))}
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>
            {mode === 'Document' ?'Document Mode: optimized for flat pages — minutes, charters, certificates. Align document edges with the frame guides.' :'Object Mode: optimized for 3D artifacts — jewels, furniture, aprons. Capture multiple angles for a complete record.'}
          </p>
        </div>

        {/* Upload / Camera area */}
        <div className="masonic-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              {mode === 'Document' ? 'Scan or Upload Document' : 'Photograph Artifact'}
            </h2>
            <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>
              {mode}
            </span>
          </div>

          {/* Drop zone */}
          <div
            className="rounded-lg border-2 border-dashed flex flex-col items-center justify-center py-10 px-6 text-center cursor-pointer transition-all duration-150"
            style={{
              borderColor: isDragging ? 'var(--accent)' : 'var(--border)',
              background: isDragging ? 'rgba(201, 168, 76, 0.05)' : 'var(--muted)',
              minHeight: 200,
            }}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'rgba(27, 42, 74, 0.08)' }}
            >
              <Icon
                name={mode === 'Document' ? 'DocumentArrowUpIcon' : 'CameraIcon'}
                size={28}
                style={{ color: 'var(--primary)' } as React.CSSProperties}
              />
            </div>
            <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>
              {isDragging ? 'Release to upload' : 'Drag files here or click to browse'}
            </p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Accepts JPG, PNG, WEBP, PDF · Max 50MB per file
            </p>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".jpg,.jpeg,.png,.webp,.pdf"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {/* Camera button */}
          <button
            type="button"
            className="masonic-btn-outline w-full mt-3"
            onClick={() => toast.info('Camera capture requires a mobile device or webcam')}
          >
            <Icon name="CameraIcon" size={18} />
            Open Camera
          </button>

          {/* File list */}
          {uploadedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
                {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} queued
              </p>
              {uploadedFiles.map((f, i) => (
                <div
                  key={`file-${i}-${f.name}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                >
                  <Icon
                    name={f.type === 'application/pdf' ? 'DocumentTextIcon' : 'PhotoIcon'}
                    size={16}
                    style={{ color: 'var(--primary)', flexShrink: 0 } as React.CSSProperties}
                  />
                  <span className="text-sm flex-1 truncate" style={{ color: 'var(--foreground)' }}>
                    {f.name}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {(f.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="p-0.5 rounded hover:bg-card transition-colors"
                  >
                    <Icon name="XMarkIcon" size={14} style={{ color: 'var(--muted-foreground)' } as React.CSSProperties} />
                  </button>
                </div>
              ))}

              {/* Run Digital Scribe button */}
              <button
                type="button"
                className="masonic-btn-gold w-full mt-2"
                onClick={handleRunScribe}
                disabled={isAnalysing}
              >
                {isAnalysing ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    Digital Scribe analysing…
                  </>
                ) : (
                  <>
                    <Icon name="SparklesIcon" size={16} />
                    Run Digital Scribe
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Digital Scribe Results Panel */}
        {scribeResult && (
          <div className="masonic-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="SparklesIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                <h3 className="font-serif text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                  Digital Scribe Results
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>
                  Confidence
                </span>
                <span
                  className="text-lg font-bold tabular-nums font-serif"
                  style={{ color: confidenceColor(scribeResult.overallConfidence) }}
                >
                  {scribeResult.overallConfidence}%
                </span>
              </div>
            </div>

            {/* Confidence bar */}
            <div className="h-1.5 rounded-full" style={{ background: 'var(--border)' }}>
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: `${scribeResult.overallConfidence}%`,
                  background: confidenceColor(scribeResult.overallConfidence),
                }}
              />
            </div>

            {/* Extracted entities */}
            {scribeResult.entities.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Extracted Entities ({scribeResult.entities.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {scribeResult.entities.map((ent, i) => {
                    const cfg = entityTypeColors[ent.type] ?? entityTypeColors.name;
                    return (
                      <div
                        key={`scribe-ent-${i}`}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium"
                        style={{ background: cfg.bg, color: cfg.color }}
                      >
                        <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={12} />
                        <span>{ent.value}</span>
                        <span className="opacity-70 tabular-nums">{ent.confidence}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div
              className="rounded-lg p-3 text-xs"
              style={{ background: 'rgba(201, 168, 76, 0.08)', border: '1px solid rgba(201, 168, 76, 0.25)', color: 'var(--muted-foreground)' }}
            >
              <p className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>Summary</p>
              <p className="leading-relaxed">{scribeResult.summary}</p>
            </div>

            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              ✓ Date and description fields auto-populated from extraction. Review before submitting.
            </p>
          </div>
        )}

        {/* Object mode guide */}
        {mode === 'Object' && (
          <div className="masonic-card p-5">
            <h3 className="font-serif text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
              Artifact Photography Guide
            </h3>
            <div className="space-y-2">
              {[
                { angle: 'Front view', desc: 'Primary face, full frame' },
                { angle: 'Rear view', desc: 'Back surface, any inscriptions' },
                { angle: 'Detail close-up', desc: 'Engravings, hallmarks, damage' },
                { angle: 'Scale reference', desc: 'Item beside a ruler or coin' },
              ].map((g, i) => (
                <div key={`guide-${i}`} className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'var(--primary)', color: 'var(--accent)' }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{g.angle}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{g.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: Metadata form */}
      <div className="xl:col-span-3">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Step indicator */}
          <div className="masonic-card p-4">
            <div className="flex items-center gap-4">
              {[1, 2].map((s) => (
                <button
                  key={`step-${s}`}
                  type="button"
                  onClick={() => setStep(s as 1 | 2)}
                  className="flex items-center gap-2"
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-colors"
                    style={{
                      background: step >= s ? 'var(--primary)' : 'var(--muted)',
                      color: step >= s ? 'var(--accent)' : 'var(--muted-foreground)',
                    }}
                  >
                    {s}
                  </div>
                  <span className="text-sm font-medium" style={{ color: step === s ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                    {s === 1 ? 'Core Metadata' : 'Provenance & Condition'}
                  </span>
                </button>
              ))}
              <div
                className="flex-1 h-0.5 mx-2"
                style={{ background: step === 2 ? 'var(--accent)' : 'var(--border)' }}
              />
            </div>
          </div>

          {step === 1 && (
            <div className="masonic-card p-6 space-y-5">
              <div
                className="flex items-center gap-2 pb-4"
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <Icon name="DocumentTextIcon" size={18} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                <h2 className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                  Core Record Metadata
                </h2>
              </div>

              <div>
                <label className="masonic-label">Record Title *</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  A descriptive title that will identify this record in the archive
                </p>
                <input
                  className="masonic-input"
                  placeholder="e.g. Meeting Minutes — Regular Communication, 14 April 1887"
                  {...register('title', { required: 'Record title is required' })}
                />
                {errors.title && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--danger)' }}>
                    {errors.title.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="masonic-label">Category *</label>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Select the drawer this record belongs in
                  </p>
                  <select
                    className="masonic-select"
                    {...register('category', { required: 'Please select a category' })}
                  >
                    <option value="">Select category…</option>
                    {categories.map((c) => (
                      <option key={`cat-opt-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--danger)' }}>
                      {errors.category.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="masonic-label">Estimated Decade</label>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Approximate period if exact date is unknown
                  </p>
                  <select
                    className="masonic-select"
                    {...register('estimatedDecade')}
                  >
                    <option value="">Select decade…</option>
                    {['1760s','1770s','1780s','1790s','1800s','1810s','1820s','1830s','1840s','1850s','1860s','1870s','1880s','1890s','1900s','1910s','1920s','1930s','1940s','1950s','1960s','1970s','1980s','1990s','2000s'].map((d) => (
                      <option key={`dec-opt-${d}`} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="masonic-label">Historical Date</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  The date appearing on the document, or the date the artifact was created. Use DD Month YYYY format.
                </p>
                <input
                  className="masonic-input"
                  placeholder="e.g. 14 April 1887 — or 'circa 1830' for artifacts"
                  {...register('historicalDate')}
                />
              </div>

              <div>
                <label className="masonic-label">Description</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  Describe the content, significance, or physical appearance of this record
                </p>
                <div className="relative">
                  <textarea
                    className="masonic-input resize-none"
                    rows={4}
                    placeholder="e.g. Handwritten minutes of the Regular Communication held on 14 April 1887. Records the initiation of three candidates and a vote on lodge hall repairs…"
                    {...register('description')}
                  />
                  <button
                    type="button"
                    className="absolute bottom-2 right-2 p-1.5 rounded-lg hover:bg-muted transition-colors"
                    title="Dictate description using voice-to-text"
                    onClick={() => toast.info('Voice dictation: hold button and speak')}
                  >
                    <Icon name="MicrophoneIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className="masonic-btn-primary"
                  onClick={() => setStep(2)}
                >
                  Next: Provenance & Condition
                  <Icon name="ArrowRightIcon" size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="masonic-card p-6 space-y-5">
              <div
                className="flex items-center gap-2 pb-4"
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <Icon name="InformationCircleIcon" size={18} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                <h2 className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                  Provenance & Physical Condition
                </h2>
              </div>

              <div>
                <label className="masonic-label">Physical Condition</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  Assess the current state of the original item
                </p>
                <select
                  className="masonic-select"
                  {...register('condition')}
                >
                  <option value="">Select condition…</option>
                  {conditions.map((c) => (
                    <option key={`cond-${c}`} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="masonic-label">Provenance Notes</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  Where was this item found? Who held it previously? Any known history of custody.
                </p>
                <div className="relative">
                  <textarea
                    className="masonic-input resize-none"
                    rows={3}
                    placeholder="e.g. Found in the Secretary's desk during lodge hall renovation, May 2026. Previously held by the estate of W. Bro. James Fraser…"
                    {...register('provenance')}
                  />
                  <button
                    type="button"
                    className="absolute bottom-2 right-2 p-1.5 rounded-lg hover:bg-muted transition-colors"
                    title="Dictate provenance using voice-to-text"
                    onClick={() => toast.info('Voice dictation: hold button and speak')}
                  >
                    <Icon name="MicrophoneIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                  </button>
                </div>
              </div>

              <div>
                <label className="masonic-label">Donor / Contributor Name</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  If this item was donated or submitted by a specific brother or family member
                </p>
                <input
                  className="masonic-input"
                  placeholder="e.g. W. Bro. Donald MacKenzie, or 'Estate of Margaret Fraser'"
                  {...register('donorName')}
                />
              </div>

              {/* AI notice */}
              <div
                className="rounded-lg p-4 flex gap-3"
                style={{ background: 'rgba(201, 168, 76, 0.1)', border: '1px solid rgba(201, 168, 76, 0.3)' }}
              >
                <Icon name="SparklesIcon" size={18} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 } as React.CSSProperties} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                    Digital Scribe will analyse this record
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                    {scribeResult
                      ? `Analysis complete — ${scribeResult.overallConfidence}% confidence. ${scribeResult.entities.length} entities extracted. Review the results in the left panel.`
                      : 'After upload, use the "Run Digital Scribe" button to extract dates, names, and produce a full transcription using OpenAI GPT-4o Vision.'}
                  </p>
                </div>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  type="button"
                  className="masonic-btn-outline"
                  onClick={() => setStep(1)}
                >
                  <Icon name="ArrowLeftIcon" size={16} />
                  Back
                </button>
                <button
                  type="submit"
                  className="masonic-btn-gold flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                      Uploading & queuing transcription…
                    </>
                  ) : (
                    <>
                      <Icon name="CloudArrowUpIcon" size={18} />
                      Submit to Archive Queue
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        {/* Lodge context */}
        <div className="masonic-card p-4 mt-5">
          <div className="flex items-center gap-3">
            <Icon name="BuildingLibraryIcon" size={18} style={{ color: 'var(--accent)' } as React.CSSProperties} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                Filing into: St. Andrew's Lodge No. 1
              </p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                Nova Scotia · District IV · Secretary: R. Sinclair
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}